/**
 * Day timeline — visuals locked to docs/prototypes/agenda-day-v1.html
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import type { Appointment, AppointmentResource, AppointmentStatus, ResourceTimeOff } from '../domain/types';
import { agenda, agendaDotColor, agendaFonts } from '../domain/agendaTheme';
import {
  boardHeightPx,
  blockLayout,
  DAY_BOARD_GUTTER_WIDTH,
  DAY_BOARD_PX_PER_MINUTE,
  DAY_BOARD_SLOT_MINUTES,
  hasBookableMinutes,
  hourLabels,
  isMinuteBookable,
  minutesToHm,
  resolveDayBoardWindow,
  resourceOpenWindows,
  timeOffEntriesForBoard,
  timeOffWindowsForLocalDay,
} from '../domain/dayBoardLayout';
import { moveTargetMinutesForResource } from '../domain/slotOptions';
import { formatQuoteSessionMeta } from '../../quotes/domain/sessionProgress';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { ticketColors } from '../../shared/theme/ticketColors';
import { VueltiDurmiendo } from '../../shared/ui/VueltiDurmiendo';

/** Breathing room under column headers so the first hour isn’t covered. */
const TIMELINE_TOP_PAD = 40;
/** Breathing room above the legend so the closing hour isn’t clipped. */
const TIMELINE_BOTTOM_PAD = 40;

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  requested: 'Solicitado',
  confirmed: 'Confirmado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  no_show: 'No vino',
};

const STATUS_STYLE: Record<AppointmentStatus, { bg: string; border: string; text: string }> = {
  requested: agenda.requested,
  confirmed: agenda.confirmed,
  completed: agenda.completed,
  cancelled: agenda.cancelled,
  no_show: agenda.noShow,
};

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function sessionMetaSuffix(
  a: Appointment,
  quoteSessionsTotalById: Map<string, number> | undefined,
): string {
  if (a.quoteRequestId) {
    const total = quoteSessionsTotalById?.get(a.quoteRequestId) ?? null;
    return ` · ${formatQuoteSessionMeta(a.sessionIndex, total)}`;
  }
  if (a.sessionIndex != null) return ` · Sesión ${a.sessionIndex}`;
  return '';
}

/** Proto draws hour lines at the bottom edge of the :45 slot (10:00 line under 9:45–10:00). */
function slotBottomIsHourLine(startMinute: number): boolean {
  return (startMinute + DAY_BOARD_SLOT_MINUTES) % 60 === 0;
}

function DayBoardSlot(props: {
  top: number;
  height: number;
  isHourLine: boolean;
  inHours: boolean;
  /** Modo mover: el grid queda de fondo sin press (los targets son los botones). */
  frozen?: boolean;
  onPress: () => void;
  accessibilityLabel: string;
}) {
  const [hovered, setHovered] = useState(false);
  const pressable = props.inHours && !props.frozen;

  return (
    <Pressable
      style={({ pressed }) => [
        styles.slot,
        { top: props.top, height: props.height },
        props.isHourLine ? styles.slotHour : styles.slotQuarter,
        !props.inHours && styles.slotClosed,
        pressable && (hovered || pressed) && styles.slotHover,
      ]}
      disabled={!pressable}
      onPress={pressable ? props.onPress : undefined}
      onHoverIn={() => pressable && setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={props.inHours ? props.accessibilityLabel : `${props.accessibilityLabel} — cerrado`}
      accessibilityState={{ disabled: !pressable }}
    />
  );
}

export type DayBoardSlotPress = { resourceId: string; startMinutes: number };
export type DayBoardMoveTarget = { resourceId: string; startMinutes: number };

/**
 * Proto .slot-target: un hueco contiguo = una zona dashed (union de todos los
 * lugares donde el turno entra completo). Los strips de 15min dan precisión de
 * click; el hover pinta el "fantasma" sólido del turno en ese inicio exacto.
 */
function MoveTargetsLayer(props: {
  targets: number[];
  durationMinutes: number;
  crossAgenda: boolean;
  resourceName: string;
  /** Touch safety: destino marcado por el primer tap — el segundo confirma. */
  previewStart: number | null;
  yAt: (minute: number) => number;
  onPress: (startMinutes: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const durH = props.durationMinutes * DAY_BOARD_PX_PER_MINUTE;
  const slotPx = DAY_BOARD_SLOT_MINUTES * DAY_BOARD_PX_PER_MINUTE;

  const runs: { start: number; lastStart: number }[] = [];
  for (const m of props.targets) {
    const last = runs[runs.length - 1];
    if (last && m - last.lastStart <= props.durationMinutes) last.lastStart = m;
    else runs.push({ start: m, lastStart: m });
  }

  const preview = props.previewStart;

  return (
    <>
      {runs.map((run) => (
        <View
          key={`run-${run.start}`}
          pointerEvents="none"
          style={[
            styles.targetZone,
            {
              top: props.yAt(run.start),
              height: (run.lastStart - run.start) * DAY_BOARD_PX_PER_MINUTE + durH,
            },
          ]}
        />
      ))}
      {hovered != null && hovered !== preview ? (
        <View
          pointerEvents="none"
          style={[
            styles.targetZone,
            styles.targetZoneFull,
            { top: props.yAt(hovered), height: durH },
          ]}
        >
          {durH >= 34 ? (
            <Text style={styles.targetText} numberOfLines={2}>
              {minutesToHm(hovered)} → acá entra
              {props.crossAgenda ? ' · cambia de agenda' : ''}
            </Text>
          ) : null}
        </View>
      ) : null}
      {preview != null ? (
        <View
          pointerEvents="none"
          style={[
            styles.targetZone,
            styles.targetZonePreview,
            { top: props.yAt(preview), height: durH },
          ]}
        >
          <Text style={styles.targetText} numberOfLines={3}>
            {minutesToHm(preview)} · tocá de nuevo para confirmar
            {props.crossAgenda ? ` (pasa a ${props.resourceName})` : ''}
          </Text>
        </View>
      ) : null}
      {props.targets.map((m) => (
        <Pressable
          key={`tgt-${m}`}
          style={[styles.targetStrip, { top: props.yAt(m), height: slotPx }]}
          onPress={() => props.onPress(m)}
          onHoverIn={() => setHovered(m)}
          onHoverOut={() => setHovered((h) => (h === m ? null : h))}
          accessibilityRole="button"
          accessibilityLabel={
            m === preview
              ? `Confirmar reagendar a ${minutesToHm(m)} ${props.resourceName}`
              : `Reagendar a ${minutesToHm(m)} ${props.resourceName}`
          }
        />
      ))}
    </>
  );
}

type Props = {
  day: Date;
  /** Desktop workbench — always show staff names in column headers (even 1 col). */
  isWide?: boolean;
  visibleResources: AppointmentResource[];
  allResources: AppointmentResource[];
  appointments: Appointment[];
  /** Branch time-off overlapping this day (filtered per column). */
  timeOffs?: ResourceTimeOff[];
  /** Pref local — los cancelados se ven apagados (ghost) en lugar de ocultarse. */
  showCancelled?: boolean;
  productNameById: Map<string, string>;
  /** estimated_sessions by quote id — batch-loaded; omit → Sesión k without /N. */
  quoteSessionsTotalById?: Map<string, number>;
  /** Modo mover: el turno en tránsito — los huecos que lo contienen entero se pintan de targets. */
  moving?: { appointmentId: string; resourceId: string; durationMinutes: number } | null;
  /** Touch safety: destino pre-marcado (tap 1 pinta ghost, tap 2 confirma). */
  movePreview?: DayBoardMoveTarget | null;
  onPressSlot: (slot: DayBoardSlotPress) => void;
  onPressAppointment: (appointment: Appointment) => void;
  /** Entrada alternativa al modo mover (proto: long-press sobre el bloque). */
  onLongPressAppointment?: (appointment: Appointment) => void;
  onPressMoveTarget?: (target: DayBoardMoveTarget) => void;
  /** PELU-09: banner "N en espera · Ofrecer ›" sobre el primer hueco que matchea la cola. */
  waitlistBanners?: Map<string, { startMinutes: number; count: number }>;
  onPressWaitlistBanner?: (resourceId: string) => void;
  selectedAppointmentId: string | null;
  refreshing?: boolean;
  onRefresh?: () => void;
};

export function AppointmentsDayBoard(props: Props) {
  const dayOfWeek = props.day.getDay();
  const cols = props.visibleResources;
  const isWide = props.isWide === true;
  const indexById = useMemo(() => {
    const m = new Map<string, number>();
    props.allResources.forEach((r, i) => m.set(r.id, i));
    return m;
  }, [props.allResources]);
  // Tick cada 30s para que la línea de "ahora" avance sin recargar datos.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  const isToday =
    props.day.getFullYear() === now.getFullYear() &&
    props.day.getMonth() === now.getMonth() &&
    props.day.getDate() === now.getDate();

  // La ventana sigue a las agendas visibles (ocultar una columna achica el rango).
  const window = useMemo(() => {
    const visibleIds = new Set(props.visibleResources.map((r) => r.id));
    const windowAppts = props.appointments.filter((a) => visibleIds.has(a.resourceId));
    return resolveDayBoardWindow(dayOfWeek, props.visibleResources, windowAppts);
  }, [dayOfWeek, props.visibleResources, props.appointments]);

  const height = boardHeightPx(window);
  const bodyH = height + TIMELINE_TOP_PAD + TIMELINE_BOTTOM_PAD;
  const labels = hourLabels(window);
  const slotCount = Math.ceil((window.endMin - window.startMin) / DAY_BOARD_SLOT_MINUTES);
  const yAt = (minute: number) =>
    TIMELINE_TOP_PAD + (minute - window.startMin) * DAY_BOARD_PX_PER_MINUTE;

  const nowTop = useMemo(() => {
    if (!isToday) return null;
    const nowMin = now.getHours() * 60 + now.getMinutes();
    if (nowMin < window.startMin || nowMin > window.endMin) return null;
    return TIMELINE_TOP_PAD + (nowMin - window.startMin) * DAY_BOARD_PX_PER_MINUTE;
  }, [isToday, window, now]);

  const appointmentsByResource = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of props.appointments) {
      if (a.status === 'cancelled' && !props.showCancelled) continue;
      const list = map.get(a.resourceId) ?? [];
      list.push(a);
      map.set(a.resourceId, list);
    }
    return map;
  }, [props.appointments, props.showCancelled]);

  const timeOffByResource = useMemo(() => {
    const map = new Map<string, ResourceTimeOff[]>();
    for (const t of props.timeOffs ?? []) {
      const list = map.get(t.resourceId) ?? [];
      list.push(t);
      map.set(t.resourceId, list);
    }
    return map;
  }, [props.timeOffs]);

  if (cols.length === 0) {
    return (
      <View style={styles.empty}>
        <VueltiDurmiendo size={80} />
        <Text style={styles.emptyText}>Elegí al menos una agenda para ver el día.</Text>
      </View>
    );
  }

  // Mobile 1-col: name lives in AgendaVisibleBar ("Viendo"). Desktop always shows col names.
  const showColNames = isWide || cols.length > 1;
  const headerH = showColNames ? agenda.headerH : 28;

  const headerRow = (
    <View style={[styles.headerRow, Platform.OS === 'web' && styles.headerSticky]}>
      <View style={[styles.gutter, styles.timeCol, styles.headerGutter, { height: headerH }]} />
      {cols.map((r) => {
        const openWins = resourceOpenWindows(dayOfWeek, r.hours);
        const blockedWins = timeOffWindowsForLocalDay(props.day, timeOffByResource.get(r.id) ?? []);
        const bookable = hasBookableMinutes(openWins, blockedWins);
        const turnCount = (appointmentsByResource.get(r.id) ?? []).length;
        // Proto: "9–18 · 3 turnos" — horario laboral + carga del día.
        const hoursLabel =
          openWins.length > 0
            ? `${Math.floor(openWins[0]!.startMin / 60)}–${Math.floor(
                openWins[openWins.length - 1]!.endMin / 60,
              )}`
            : null;
        const hint = bookable
          ? `${hoursLabel} · ${turnCount} ${turnCount === 1 ? 'turno' : 'turnos'}`
          : openWins.length > 0 && blockedWins.length > 0
            ? `Ausente${hoursLabel ? ` · ${hoursLabel}` : ''}`
            : 'Cerrado hoy';
        const dot = agendaDotColor(r.color, indexById.get(r.id) ?? 0);
        return (
          <View key={r.id} style={[styles.colHeader, !bookable && styles.colNarrow, { height: headerH }]}>
            {showColNames ? (
              <View style={styles.colHeaderNameRow}>
                <View style={[styles.headDot, { backgroundColor: dot }]} />
                <Text style={styles.colHeaderName} numberOfLines={1}>
                  {r.displayName}
                  {r.resourceKind === 'bay'
                    ? ' · Lift'
                    : r.resourceKind === 'equipment'
                      ? ' · Equipo'
                      : ''}
                </Text>
              </View>
            ) : null}
            <Text style={[styles.colHeaderHint, !showColNames && styles.colHeaderHintSolo]}>
              {hint}
            </Text>
          </View>
        );
      })}
    </View>
  );

  const gridBody = (
    <View style={[styles.gridBody, { height: bodyH }]}>
      <View style={[styles.gutter, styles.timeCol, { height: bodyH }]}>
        {labels.map((l) => {
          const lineTop = yAt(l.minute);
          // Center on the hour line; keep inside top/bottom breathing room.
          const labelTop = Math.min(
            bodyH - TIMELINE_BOTTOM_PAD + 2,
            Math.max(TIMELINE_TOP_PAD - 2, lineTop - 7),
          );
          return (
            <Text key={l.minute} style={[styles.hourLabel, { top: labelTop }]}>
              {l.label}
            </Text>
          );
        })}
      </View>

      {cols.map((r) => {
        const openWins = resourceOpenWindows(dayOfWeek, r.hours);
        const blockedWins = timeOffWindowsForLocalDay(props.day, timeOffByResource.get(r.id) ?? []);
        const offEntries = timeOffEntriesForBoard(
          props.day,
          window,
          timeOffByResource.get(r.id) ?? [],
        );
        const colAppts = appointmentsByResource.get(r.id) ?? [];
        const moveTargets = props.moving
          ? moveTargetMinutesForResource({
              dayOfWeek,
              resource: r,
              day: props.day,
              timeOffs: timeOffByResource.get(r.id) ?? [],
              appointments: props.appointments,
              durationMinutes: props.moving.durationMinutes,
              excludeId: props.moving.appointmentId,
            })
          : [];

        const bookable = hasBookableMinutes(openWins, blockedWins);
        return (
          <View key={r.id} style={[styles.column, !bookable && styles.colNarrow, { height: bodyH }]}>
            {Array.from({ length: slotCount }, (_, i) => {
              const minute = window.startMin + i * DAY_BOARD_SLOT_MINUTES;
              const inHours = isMinuteBookable(minute, openWins, blockedWins);
              const isHourLine = slotBottomIsHourLine(minute);
              return (
                <DayBoardSlot
                  key={minute}
                  top={yAt(minute)}
                  height={DAY_BOARD_SLOT_MINUTES * DAY_BOARD_PX_PER_MINUTE}
                  isHourLine={isHourLine}
                  inHours={inHours}
                  frozen={props.moving != null}
                  onPress={() => props.onPressSlot({ resourceId: r.id, startMinutes: minute })}
                  accessibilityLabel={`Crear turno ${minutesToHm(minute)} ${r.displayName}`}
                />
              );
            })}

            {(() => {
              const banner = props.moving == null ? props.waitlistBanners?.get(r.id) : undefined;
              if (!banner) return null;
              const top = yAt(banner.startMinutes);
              return (
                <TouchableOpacity
                  style={[styles.waitlistBanner, { top }]}
                  onPress={() => props.onPressWaitlistBanner?.(r.id)}
                  accessibilityRole="button"
                  accessibilityLabel={`${banner.count} en espera — ofrecer turno en ${r.displayName}`}
                  testID={`appointments.waitlist.banner.${r.id}`}
                >
                  <Text style={styles.waitlistBannerText} numberOfLines={1}>
                    {banner.count} en espera · Ofrecer ›
                  </Text>
                </TouchableOpacity>
              );
            })()}

            {props.moving ? (
              <MoveTargetsLayer
                targets={moveTargets}
                durationMinutes={props.moving.durationMinutes}
                crossAgenda={r.id !== props.moving.resourceId}
                resourceName={r.displayName}
                previewStart={
                  props.movePreview?.resourceId === r.id ? props.movePreview.startMinutes : null
                }
                yAt={yAt}
                onPress={(m) => props.onPressMoveTarget?.({ resourceId: r.id, startMinutes: m })}
              />
            ) : null}

            {offEntries.map((e, i) => {
              const h = (e.endMin - e.startMin) * DAY_BOARD_PX_PER_MINUTE;
              return (
                <View
                  key={`off-${i}`}
                  pointerEvents="none"
                  style={[
                    styles.offBlock,
                    {
                      top: TIMELINE_TOP_PAD + (e.startMin - window.startMin) * DAY_BOARD_PX_PER_MINUTE,
                      height: h,
                    },
                  ]}
                  accessibilityLabel={`${e.label} ${minutesToHm(e.startMin)}–${minutesToHm(e.endMin)}`}
                >
                  <Text style={styles.offLabel} numberOfLines={1}>
                    {e.label}
                  </Text>
                  {h > 30 ? (
                    <Text style={styles.offRange} numberOfLines={1}>
                      {minutesToHm(e.startMin)}–{minutesToHm(e.endMin)}
                    </Text>
                  ) : null}
                </View>
              );
            })}

            {colAppts.map((a) => {
              const layout = blockLayout(window, a.startsAt, a.endsAt);
              const st = STATUS_STYLE[a.status] ?? STATUS_STYLE.confirmed;
              const cancelled = a.status === 'cancelled';
              const selected = props.selectedAppointmentId === a.id;
              const isMovingBlock = props.moving?.appointmentId === a.id;
              const serviceName = props.productNameById.get(a.productId);
              const sessionSuffix = sessionMetaSuffix(a, props.quoteSessionsTotalById);
              const arrivedLabel = a.arrivedAt ? '✓ Llegó · ' : '';
              const movingLabel = isMovingBlock ? ' — moviendo' : '';
              // Chips proto: SEÑA = seña paga, PAQ = sesión de paquete.
              const chips: string[] = [];
              if (!cancelled) {
                if (a.paymentStatus === 'deposit_paid') chips.push('SEÑA');
                if (a.packageEntitlementId) chips.push('PAQ');
              }
              // ponytail: 15-min slot ≈ 22px — pack one line; don't invent taller blocks.
              const density: 'short' | 'medium' | 'tall' =
                layout.height < DAY_BOARD_SLOT_MINUTES * DAY_BOARD_PX_PER_MINUTE * 1.75
                  ? 'short'
                  : layout.height < DAY_BOARD_SLOT_MINUTES * DAY_BOARD_PX_PER_MINUTE * 2.75
                    ? 'medium'
                    : 'tall';
              return (
                <TouchableOpacity
                  key={a.id}
                  style={[
                    styles.block,
                    density === 'short' && styles.blockShort,
                    density === 'medium' && styles.blockMedium,
                    {
                      top: layout.top + TIMELINE_TOP_PAD,
                      height: layout.height,
                      backgroundColor: st.bg,
                      borderColor: selected ? agenda.brand : st.border,
                      borderWidth: selected ? 2 : 1,
                    },
                    a.arrivedAt && !cancelled && styles.blockArrived,
                    cancelled && styles.blockGhost,
                    isMovingBlock && styles.blockMoving,
                  ]}
                  activeOpacity={0.85}
                  disabled={props.moving != null}
                  onPress={() => props.onPressAppointment(a)}
                  onLongPress={
                    props.moving == null && props.onLongPressAppointment && !cancelled
                      ? () => props.onLongPressAppointment?.(a)
                      : undefined
                  }
                  accessibilityRole="button"
                  accessibilityLabel={`${formatTime(a.startsAt)} ${arrivedLabel}${STATUS_LABELS[a.status]} ${a.customerName}${serviceName ? ` ${serviceName}` : ''}`}
                >
                  {density === 'short' ? (
                    <Text
                      style={[styles.blockCompact, { color: st.text }, cancelled && styles.textGhost]}
                      numberOfLines={1}
                    >
                      {arrivedLabel}{formatTime(a.startsAt)} · {a.customerName}
                      {serviceName ? ` · ${serviceName}` : ''}
                      {sessionSuffix}
                      {movingLabel}
                    </Text>
                  ) : density === 'medium' ? (
                    <>
                      <View style={styles.blockTimeRow}>
                        <Text
                          style={[styles.blockTime, styles.blockTimeFlex, { color: st.text }]}
                          numberOfLines={1}
                        >
                          {arrivedLabel}{formatTime(a.startsAt)} · {STATUS_LABELS[a.status]}
                          {sessionSuffix}
                        </Text>
                        {chips.map((c) => (
                          <View key={c} style={styles.blockChip}>
                            <Text style={[styles.blockChipText, { color: st.text }]}>{c}</Text>
                          </View>
                        ))}
                      </View>
                      <Text
                        style={[
                          styles.blockCustomer,
                          { color: st.text },
                          cancelled && styles.textGhost,
                        ]}
                        numberOfLines={1}
                      >
                        {a.customerName}
                        {serviceName ? ` · ${serviceName}` : ''}
                        {movingLabel}
                      </Text>
                    </>
                  ) : (
                    <>
                      <View style={styles.blockTimeRow}>
                        <Text
                          style={[styles.blockTime, styles.blockTimeFlex, { color: st.text }]}
                          numberOfLines={1}
                        >
                          {arrivedLabel}{formatTime(a.startsAt)} · {STATUS_LABELS[a.status]}
                        </Text>
                        {chips.map((c) => (
                          <View key={c} style={styles.blockChip}>
                            <Text style={[styles.blockChipText, { color: st.text }]}>{c}</Text>
                          </View>
                        ))}
                      </View>
                      <Text
                        style={[
                          styles.blockCustomer,
                          { color: st.text },
                          cancelled && styles.textGhost,
                        ]}
                        numberOfLines={2}
                      >
                        {a.customerName}
                      </Text>
                      {a.quoteRequestId ? (
                        <Text style={[styles.blockService, { color: st.text }]} numberOfLines={1}>
                          {formatQuoteSessionMeta(
                            a.sessionIndex,
                            props.quoteSessionsTotalById?.get(a.quoteRequestId) ?? null,
                          )}
                          {serviceName ? ` · ${serviceName}` : ''}
                        </Text>
                      ) : serviceName || a.sessionIndex != null ? (
                        <Text style={[styles.blockService, { color: st.text }]} numberOfLines={1}>
                          {serviceName ?? ''}
                          {a.sessionIndex != null
                            ? `${serviceName ? ' · ' : ''}Sesión ${a.sessionIndex}`
                            : ''}
                          {movingLabel}
                        </Text>
                      ) : null}
                    </>
                  )}
                </TouchableOpacity>
              );
            })}

            {nowTop != null ? (
              <View style={[styles.nowLine, { top: nowTop }]} pointerEvents="none">
                <View style={styles.nowDot} />
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );

  const refreshControl = props.onRefresh ? (
    <RefreshControl refreshing={Boolean(props.refreshing)} onRefresh={props.onRefresh} />
  ) : undefined;

  return (
    <View style={styles.root}>
      <PaperSheet style={styles.timelineSheet}>
        <View style={styles.boardInner}>
          {Platform.OS === 'web' ? (
            <ScrollView
              style={styles.vScroll}
              showsVerticalScrollIndicator
              contentContainerStyle={{ minHeight: bodyH + headerH + 8 }}
              refreshControl={refreshControl}
            >
              {headerRow}
              {gridBody}
            </ScrollView>
          ) : (
            <>
              {headerRow}
              <ScrollView
                style={styles.vScroll}
                showsVerticalScrollIndicator
                contentContainerStyle={{ minHeight: bodyH + 8 }}
                refreshControl={refreshControl}
              >
                {gridBody}
              </ScrollView>
            </>
          )}
        </View>

        <View style={styles.legend}>
          <LegendSwatch color={agenda.requested.bg} border={agenda.requested.border} label="Solicitado" />
          <LegendSwatch color={agenda.confirmed.bg} border={agenda.confirmed.border} label="Confirmado" />
          <LegendSwatch color={agenda.completed.bg} border={agenda.completed.border} label="Completado" />
          <LegendSwatch color={agenda.noShow.bg} border={agenda.noShow.border} label="No vino" />
          <View style={styles.legendItem}>
            <View style={[styles.legendSwatch, styles.legendSwatchOff]} />
            <Text style={styles.legendText}>Ausencia</Text>
          </View>
          <Text style={styles.legendText}>· Gris = cerrado</Text>
        </View>
      </PaperSheet>
    </View>
  );
}

function LegendSwatch(props: { color: string; border: string; label: string }) {
  return (
    <View style={styles.legendItem}>
      <View style={[styles.legendSwatch, { backgroundColor: props.color, borderColor: props.border }]} />
      <Text style={styles.legendText}>{props.label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    backgroundColor: agenda.bg,
    padding: 10,
  },
  timelineSheet: {
    flex: 1,
    minHeight: 0,
    borderColor: ticketColors.ruleSoft,
  },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, backgroundColor: agenda.surface },
  emptyText: { fontSize: 14, color: agenda.ink2, textAlign: 'center', fontFamily: agendaFonts.sans, marginTop: 10 },
  boardInner: { flex: 1, width: '100%', alignSelf: 'stretch' },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
    width: '100%',
    zIndex: 4,
    backgroundColor: agenda.surface2,
  },
  /** Web: keep headers aligned with columns while body scrolls (avoids scrollbar drift). */
  headerSticky: Platform.select({
    web: { position: 'sticky', top: 0, zIndex: 4 } as object,
    default: {},
  }),
  gutter: {
    width: DAY_BOARD_GUTTER_WIDTH,
    flexShrink: 0,
    backgroundColor: agenda.surface,
  },
  /** Proto .time-col — vertical divider between hour labels and columns. */
  timeCol: {
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
  },
  headerGutter: {
    backgroundColor: agenda.surface2,
  },
  colHeader: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: 10,
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
    backgroundColor: agenda.surface2,
  },
  colHeaderNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  headDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },
  colHeaderName: {
    fontSize: 13,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    flex: 1,
  },
  colHeaderHint: { fontSize: 11, color: agenda.ink2, marginTop: 1, fontFamily: agendaFonts.sans },
  colHeaderHintSolo: { marginTop: 0 },
  /** Agenda sin horario/cerrada: ocupa menos que una operativa (menos gris muerto). */
  colNarrow: { flex: 0.55 },
  vScroll: { flex: 1 },
  gridBody: { flexDirection: 'row', width: '100%', alignSelf: 'stretch' },
  hourLabel: {
    position: 'absolute',
    right: 8,
    fontSize: 11,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.mono,
    zIndex: 1,
  },
  column: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
    borderRightWidth: 1,
    borderRightColor: ticketColors.ruleSoft,
    backgroundColor: agenda.surface,
  },
  slot: {
    position: 'absolute',
    left: 0,
    right: 0,
  },
  /** Proto col-body 15-min grid lines (--border, 1px). */
  slotQuarter: {
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
  },
  /** Proto col-body::before hour lines (rgba 26,31,30,.18). */
  slotHour: {
    borderBottomWidth: 1,
    borderBottomColor: agenda.hourLine,
  },
  slotClosed: {
    backgroundColor: agenda.slotClosed,
  },
  slotHover: {
    backgroundColor: agenda.slotHover,
  },
  block: {
    position: 'absolute',
    left: 4,
    right: 4,
    borderRadius: agenda.radiusBlock,
    paddingHorizontal: 7,
    paddingVertical: 5,
    zIndex: 2,
    overflow: 'hidden',
    ...Platform.select({
      web: { boxShadow: '0 1px 2px rgba(0,0,0,0.06)' } as object,
      default: {},
    }),
  },
  blockShort: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  blockArrived: {
    borderLeftWidth: 3,
    borderLeftColor: agenda.brand,
  },
  /** Cancelado visible por pref — apagado, sin sombra. */
  blockGhost: {
    opacity: 0.55,
    ...Platform.select({
      web: { boxShadow: 'none' } as object,
      default: {},
    }),
  },
  textGhost: { textDecorationLine: 'line-through' },
  /** Proto .block.moving — queda como ancla atenuada mientras se elige destino. */
  blockMoving: { opacity: 0.55 },
  /** Proto .slot-target — hueco contiguo que contiene el turno entero. */
  targetZone: {
    position: 'absolute',
    left: 4,
    right: 4,
    zIndex: 3,
    borderRadius: 6,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: agenda.brand,
    backgroundColor: 'rgba(15,118,110,0.10)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  /** Proto .slot-target.full — preview sólido del turno en el inicio hover. */
  targetZoneFull: {
    borderStyle: 'solid',
    backgroundColor: 'rgba(15,118,110,0.16)',
  },
  /** Touch tap-1: destino pre-marcado más sólido que el hover. */
  targetZonePreview: {
    borderStyle: 'solid',
    borderWidth: 2,
    backgroundColor: 'rgba(15,118,110,0.22)',
  },
  targetStrip: {
    position: 'absolute',
    left: 4,
    right: 4,
    zIndex: 4,
  },
  targetText: {
    fontSize: 11,
    fontWeight: '800',
    color: agenda.chooseInk,
    textAlign: 'center',
  },
  blockTimeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  },
  blockTimeFlex: { flex: 1, minWidth: 0 },
  /** Chip SEÑA/PAQ del proto — pill blanca translúcida dentro del bloque. */
  blockChip: {
    flexShrink: 0,
    borderRadius: 999,
    paddingHorizontal: 5,
    paddingVertical: 1,
    backgroundColor: 'rgba(255,255,255,0.75)',
  },
  blockChipText: {
    fontSize: 8.5,
    fontWeight: '700',
    letterSpacing: 0.3,
    fontFamily: agendaFonts.sans,
  },
  /** Bloque de ausencia etiquetado (proto .off-block rayado). */
  offBlock: {
    position: 'absolute',
    left: 4,
    right: 4,
    borderRadius: agenda.radiusBlock,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: agenda.ink3,
    backgroundColor: 'rgba(0,0,0,0.04)',
    paddingHorizontal: 7,
    paddingVertical: 4,
    justifyContent: 'center',
    zIndex: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        borderStyle: 'solid',
        backgroundImage:
          'repeating-linear-gradient(-45deg, rgba(28,25,23,0.07) 0, rgba(28,25,23,0.07) 5px, transparent 5px, transparent 11px)',
        backgroundColor: 'rgba(0,0,0,0.02)',
      } as object,
      default: {},
    }),
  },
  offLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  offRange: {
    fontSize: 9.5,
    color: agenda.ink3,
    fontFamily: agendaFonts.mono,
    marginTop: 1,
  },
  blockMedium: {
    paddingVertical: 3,
  },
  blockCompact: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: agendaFonts.sans,
    lineHeight: 14,
  },
  blockTime: { fontSize: 10, fontWeight: '700', fontFamily: agendaFonts.sans, opacity: 0.85 },
  blockCustomer: { fontSize: 12, fontWeight: '700', marginTop: 1, fontFamily: agendaFonts.sans },
  blockService: { fontSize: 11, marginTop: 1, opacity: 0.8, fontFamily: agendaFonts.sans },
  nowLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: agenda.now,
    zIndex: 6,
  },
  nowDot: {
    position: 'absolute',
    left: -4,
    top: -4,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: agenda.now,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: agenda.surface,
  },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  legendSwatch: {
    width: 10,
    height: 10,
    borderRadius: 3,
    borderWidth: 1,
  },
  legendSwatchOff: {
    backgroundColor: 'rgba(0,0,0,0.04)',
    borderColor: agenda.ink3,
    borderStyle: 'dashed',
  },
  legendText: { fontSize: 11, color: agenda.ink2, fontFamily: agendaFonts.sans },
  /** PELU-09 — proto: banner "N en espera · Ofrecer ›" flotando en el hueco. */
  waitlistBanner: {
    position: 'absolute',
    left: 3,
    right: 3,
    height: 26,
    borderRadius: 8,
    backgroundColor: agenda.chooseInk,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 7,
  },
  waitlistBannerText: {
    fontSize: 11.5,
    fontWeight: '800',
    color: ticketColors.goldInk,
    fontFamily: agendaFonts.sans,
  },
});
